import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDown } from "lucide-react";
import {
  FileText,
  Type,
  Image,
  Table,
  DollarSign,
  FileSignature,
  FileCheck,
  Images,
  Sparkles,
  Camera,
  Video,
  Link,
  Package,
  Square,
  Star,
} from "lucide-react";

interface BlocksPaletteProps {
  onAddBlock: (blockType: string) => void;
}

const blockCategories = [
  {
    id: "layout",
    label: "📄 Layout",
    blocks: [
      { type: "cover", label: "Cover Page", icon: FileText, description: "Full-page cover with title and background" },
    ],
  },
  {
    id: "text-media",
    label: "✏️ Text & Media",
    blocks: [
      { type: "text", label: "Text Block", icon: Type, description: "Rich text with smart fields like {{customer_name}}" },
      { type: "image", label: "Image", icon: Image, description: "Upload and position images" },
      { type: "dynamic_image", label: "Dynamic Photo", icon: Camera, description: "Placeholder for customer photos" },
      { type: "video_embed", label: "Video", icon: Video, description: "Embed YouTube or Vimeo videos" },
      { type: "marketing_gallery", label: "Photo Gallery", icon: Images, description: "Grid of marketing images" },
    ],
  },
  {
    id: "design",
    label: "🎨 Design Elements",
    blocks: [
      { type: "shape", label: "Shape", icon: Square, description: "Add rectangles, circles, lines, or arrows" },
      { type: "icon", label: "Icon", icon: Star, description: "Insert from 1000+ professional icons" },
      { type: "button", label: "Button", icon: Link, description: "Clickable call-to-action button" },
      { type: "smart_field", label: "Smart Field", icon: Sparkles, description: "Dynamic data placeholder" },
    ],
  },
  {
    id: "pricing",
    label: "💰 Pricing",
    blocks: [
      { type: "line_items_table", label: "Line Items", icon: Table, description: "Itemized pricing table" },
      { type: "package_selector", label: "Package Options", icon: Package, description: "Multiple package choices" },
      { type: "good_better_best", label: "Good/Better/Best", icon: DollarSign, description: "Three-tier pricing comparison" },
      { type: "totals_summary", label: "Totals", icon: DollarSign, description: "Price summary with tax and total" },
    ],
  },
  {
    id: "forms",
    label: "📋 Forms & Signatures",
    blocks: [
      { type: "signature", label: "Signature", icon: FileSignature, description: "Digital signature block" },
      { type: "terms", label: "Terms", icon: FileCheck, description: "Terms and conditions" },
    ],
  },
];

export function BlocksPalette({ onAddBlock }: BlocksPaletteProps) {
  const [openCategories, setOpenCategories] = useState<string[]>(["layout", "text-media"]);

  const toggleCategory = (categoryId: string) => {
    setOpenCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="h-full overflow-hidden flex flex-col">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-sm">Content Blocks</h3>
          <p className="text-xs text-muted-foreground mt-1">Drag blocks onto the canvas</p>
        </div>

      <ScrollArea className="flex-1" type="always">
        <div className="p-2 space-y-1 pr-2">
            <div className="sticky top-0 h-4 bg-gradient-to-b from-card to-transparent pointer-events-none -mb-4 z-10" />
            {blockCategories.map((category) => (
              <Collapsible
                key={category.id}
                open={openCategories.includes(category.id)}
                onOpenChange={() => toggleCategory(category.id)}
              >
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between h-8 px-2 mb-1">
                    <span className="text-xs font-medium">{category.label}</span>
                    <ChevronDown
                      className={`w-3 h-3 transition-transform ${
                        openCategories.includes(category.id) ? "rotate-180" : ""
                      }`}
                    />
                  </Button>
                </CollapsibleTrigger>

                <CollapsibleContent className="space-y-1 pb-2">
                  {category.blocks.map((block) => (
                    <div
                      key={block.type}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("blockType", block.type);
                        e.dataTransfer.effectAllowed = "copy";
                      }}
                      className="cursor-move"
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-start h-8 text-xs"
                            onClick={() => onAddBlock(block.type)}
                          >
                            <block.icon className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
                            <span className="truncate">{block.label}</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p className="font-medium">{block.label}</p>
                          <p className="text-xs text-muted-foreground max-w-[200px]">{block.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ))}
            <div className="sticky bottom-0 h-4 bg-gradient-to-t from-card to-transparent pointer-events-none -mt-4 z-10" />
          </div>
        </ScrollArea>

        <div className="p-2 border-t">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" className="w-full justify-start h-8 text-xs">
                <Sparkles className="w-3.5 h-3.5 mr-2" />
                AI Assistant
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p className="font-medium">AI Assistant</p>
              <p className="text-xs text-muted-foreground max-w-[200px]">
                Generate content with AI in the right sidebar
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}
